import { useCompany } from "../../../contexts/CompanyContext";
import { useRole } from "../../../contexts/RoleContext";

// 공통 스탯 카드 컴포넌트
function StatCard({ label, value, sub, trend }: { label: string; value: string; sub: string; trend?: string }) {
  return (
    <div className="bg-[#0C0C0E] border border-white/[0.05] rounded-xl p-4">
      <div className="text-[#555555] text-[10px] uppercase tracking-widest mb-2">{label}</div>
      <div className="text-[#F2F2F2] text-xl font-semibold tabular-nums mb-1">{value}</div>
      <div className="text-[#444444] text-[11px]">{sub}</div>
      {trend && <div className="text-[#666666] text-[10px] mt-1">{trend}</div>}
    </div>
  );
}

// 공통 섹션 헤더
function SectionHeader({ title, action }: { title: string; action?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-[#888888] text-xs font-medium uppercase tracking-widest">{title}</h3>
      {action}
    </div>
  );
}

// ─── CEO 대시보드 ───────────────────────────────────
export function CEODashboard() {
  const { companyState } = useCompany();
  const capital = companyState?.capital ?? 0;
  const reputation = companyState?.reputation ?? 0;
  const creditScore = companyState?.credit_score ?? 80;

  return (
    <div className="space-y-5">
      {/* 핵심 지표 */}
      <div className="grid grid-cols-4 gap-3">
        <StatCard label="가상 자본금" value={`${capital.toLocaleString()}만원`} sub="현재 잔고" />
        <StatCard label="직급 레벨" value={reputation >= 81 ? "팀장" : reputation >= 61 ? "과장" : reputation >= 41 ? "대리" : reputation >= 21 ? "사원" : "인턴"} sub={`경력 점수 ${reputation}점`} trend={reputation >= 70 ? "승진 임박" : "성장 중"} />
        <StatCard label="신용도" value={`${creditScore}점`} sub="금융 신뢰도" />
        <StatCard label="대기 결재" value="3건" sub="서명 필요" trend="오늘 기한 1건" />
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* 이번 주 크로스팀 경영 이슈 */}
        <div>
          <SectionHeader title="이번 주 경영 핵심 이슈" />
          <div className="bg-[#141416] border border-white/[0.05] rounded-xl divide-y divide-white/[0.04]">
            {[
              { dept: "재무팀", issue: "DarkNova MV 예산안 8,000만원 최종 결재 대기", level: "긴급", icon: "ri-money-dollar-circle-line" },
              { dept: "법무팀", issue: "스타라이트엔터 표절 시비 내용증명 수신 — 대응 전략 보고 요청", level: "긴급", icon: "ri-alert-line" },
              { dept: "마케팅팀", issue: "루나&스타즈 봄 시즌 캠페인 중간 성과 보고 (A+ 등급)", level: "일반", icon: "ri-megaphone-line" },
              { dept: "A&R팀", issue: "루나&스타즈 EP 타이틀곡 최종 선곡 완료 — CEO 최종 승인 요청", level: "일반", icon: "ri-music-line" },
              { dept: "공연기획", issue: "K-POP 페스티벌 대타 출연 계약서 서명 필요 (기한: 오늘)", level: "긴급", icon: "ri-mic-line" },
            ].map((item, i) => (
              <div key={i} className="px-4 py-3 flex items-center gap-3">
                <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-white/[0.04] shrink-0">
                  <i className={`${item.icon} text-[#555555] text-sm`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-[10px] text-[#7A9BFF] bg-[#4B6CF7]/10 border border-[#4B6CF7]/15 px-1.5 py-0.5 rounded shrink-0">{item.dept}</span>
                    {item.level === "긴급" && (
                      <span className="text-[9px] text-white/40 border border-white/15 px-1.5 py-0.5 rounded shrink-0">긴급</span>
                    )}
                  </div>
                  <p className="text-[#CCCCCC] text-xs leading-snug">{item.issue}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          {/* 부서 현황 */}
          <div>
            <SectionHeader title="부서 현황" />
            <div className="grid grid-cols-2 gap-2">
              {[
                { name: "A&R 허브", status: "정상", tasks: 3, icon: "ri-music-2-line", href: "/ar-hub" },
                { name: "재무 / ERP", status: "주의", tasks: 2, icon: "ri-bank-line", href: "/finance" },
                { name: "마케팅 워룸", status: "정상", tasks: 5, icon: "ri-megaphone-line", href: "/marketing" },
                { name: "법무 & IP", status: "긴급", tasks: 1, icon: "ri-file-shield-2-line", href: "/legal" },
              ].map((dept) => (
                <a key={dept.name} href={dept.href}
                  className="bg-[#141416] border border-white/[0.05] rounded-xl p-3 flex items-center gap-2.5 hover:border-white/[0.12] transition-colors cursor-pointer"
                >
                  <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-white/[0.04] shrink-0">
                    <i className={`${dept.icon} text-[#555555] text-sm`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[#CCCCCC] text-xs font-medium truncate">{dept.name}</div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className={`text-[9px] px-1 py-0.5 rounded border ${
                        dept.status === "긴급" ? "text-white/50 border-white/20" :
                        dept.status === "주의" ? "text-white/30 border-white/10" :
                        "text-[#444444] border-white/[0.05]"
                      }`}>{dept.status}</span>
                      <span className="text-[#555555] text-[9px]">미완료 {dept.tasks}건</span>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>

          {/* 결재 대기 */}
          <div>
            <SectionHeader title="결재 대기" action={<a href="/approval" className="text-[#444444] text-[10px] hover:text-[#666666] cursor-pointer">전체 보기 →</a>} />
            <div className="bg-[#141416] border border-white/[0.05] rounded-xl divide-y divide-white/[0.04]">
              {[
                { title: "DarkNova MV 예산안", dept: "재무 → CEO", amount: "8,000만원", urgent: true },
                { title: "루나&스타즈 전속 계약서", dept: "법무 → CEO", amount: "계약금 2,000만원", urgent: false },
                { title: "SNS 광고 집행 기안서", dept: "마케팅 → CEO", amount: "500만원", urgent: false },
              ].map((item, i) => (
                <div key={i} className="px-4 py-2.5 flex items-center gap-3">
                  {item.urgent && <div className="w-1 h-5 bg-white/30 rounded-full shrink-0" />}
                  {!item.urgent && <div className="w-1 h-5 rounded-full shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-[#CCCCCC] text-xs font-medium truncate">{item.title}</div>
                    <div className="text-[#555555] text-[10px]">{item.dept}</div>
                  </div>
                  <div className="text-[#555555] text-xs tabular-nums shrink-0">{item.amount}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── A&R 대시보드 ───────────────────────────────────
export function ARDashboard() {
  const { sendNotification } = useRole();

  const handleRequestBudget = async () => {
    await sendNotification("Finance", "budget_request", "[A&R] 데모 수급 예산 요청", "신규 데모곡 발굴을 위해 500만원 예산 집행 승인을 요청합니다.", "ar-demo-01");
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-4 gap-3">
        <StatCard label="진행 중 앨범" value="2개" sub="기획 단계" />
        <StatCard label="수급 데모곡" value="14곡" sub="이번 달" trend="AI 작곡가 3명" />
        <StatCard label="기획서 평균 점수" value="78점" sub="/ 100점" trend="제출 기준 75점" />
        <StatCard label="아티스트 계약" value="3팀" sub="전속 계약 중" />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-3">
          <SectionHeader title="앨범 기획 현황" action={<a href="/ar-hub" className="text-[#444444] text-[10px] hover:text-[#666666] cursor-pointer">A&R 허브 →</a>} />
          <div className="bg-[#141416] border border-white/[0.05] rounded-xl divide-y divide-white/[0.04]">
            {[
              { title: "루나&스타즈 1st EP", stage: "데모 선곡 단계", progress: 65, deadline: "D-18" },
              { title: "DarkNova 정규 1집", stage: "콘셉트 기획 중", progress: 30, deadline: "D-42" },
            ].map((a, i) => (
              <div key={i} className="px-4 py-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[#CCCCCC] text-xs font-medium">{a.title}</span>
                  <span className="text-[#555555] text-[10px]">{a.deadline}</span>
                </div>
                <div className="text-[#444444] text-[10px] mb-2">{a.stage}</div>
                <div className="w-full bg-white/[0.04] rounded-full h-px">
                  <div className="h-px rounded-full bg-white/25" style={{ width: `${a.progress}%` }} />
                </div>
              </div>
            ))}
          </div>

          {/* 신인 발굴 파이프라인 */}
          <div className="bg-[#141416] border border-white/[0.05] rounded-xl px-4 py-3">
            <div className="text-[#555555] text-[10px] uppercase tracking-widest mb-2">신인 발굴 파이프라인</div>
            {[
              { name: "춘 지원자 1차 서류", count: "20건", stage: "검토중" },
              { name: "2차 오디션 통과자", count: "6명", stage: "심사예정" },
            ].map((item, i) => (
              <div key={i} className="flex items-center justify-between py-1.5 border-b border-white/[0.03] last:border-0">
                <span className="text-[#555555] text-xs">{item.name}</span>
                <div className="flex items-center gap-2">
                  <span className="text-[#CCCCCC] text-xs font-medium">{item.count}</span>
                  <span className="text-[#444444] text-[10px]">{item.stage}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          <SectionHeader title="크로스팀 연결 업무" />
          <div className="bg-[#141416] border border-white/[0.05] rounded-xl divide-y divide-white/[0.04]">
            {[
              { task: "루나&스타즈 EP 무드보드 → 마케팅팀 공유", team: "마케팅팀", icon: "ri-megaphone-line" },
              { task: "전속계약 갱신 조건 → 법무팀 검토 의뢰", team: "법무팀", icon: "ri-file-text-line" },
              { task: "DarkNova MV 예산안 → 재무팀 사전 협의", team: "재무팀", icon: "ri-money-dollar-circle-line" },
              { task: "CEO에게 데모 최종 선곡 결과 보고", team: "CEO", icon: "ri-user-star-line" },
            ].map((item, i) => (
              <div key={i} className="px-4 py-3 flex items-center gap-3">
                <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-white/[0.04] shrink-0">
                  <i className={`${item.icon} text-[#555555] text-sm`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[#CCCCCC] text-xs leading-snug">{item.task}</div>
                  <div className="text-[#555555] text-[10px] mt-0.5">→ {item.team}</div>
                </div>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={handleRequestBudget}
            className="w-full flex items-center justify-center gap-1.5 text-xs px-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.08] text-[#CCCCCC] hover:bg-white/[0.08] transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-bank-line"></i>재무팀에 데모 예산 500만원 요청 발송
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── 재무 대시보드 ───────────────────────────────────
export function FinanceDashboard() {
  const { companyState } = useCompany();
  const capital = companyState?.capital ?? 0;
  const creditScore = companyState?.credit_score ?? 80;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-4 gap-3">
        <StatCard label="가상 계좌 잔고" value={`${capital.toLocaleString()}만원`} sub="현재 잔고" />
        <StatCard label="이번 달 지출" value="3,200만원" sub="전월 대비 +12%" />
        <StatCard label="신용도" value={`${creditScore}점`} sub="금융 신뢰도" trend={creditScore >= 80 ? "양호" : "주의 필요"} />
        <StatCard label="세금 납부" value="D-7" sub="부가세 기한" trend="미납 시 신용도 -10" />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <SectionHeader title="예산 승인 대기" action={<a href="/finance" className="text-[#444444] text-[10px] hover:text-[#666666] cursor-pointer">ERP 보기 →</a>} />
          <div className="bg-[#141416] border border-white/[0.05] rounded-xl divide-y divide-white/[0.04]">
            {[
              { from: "A&R팀", title: "데모 수급 예산", amount: "500만원", status: "대기", urgent: false },
              { from: "마케팅팀", title: "SNS 광고 집행", amount: "300만원", status: "대기", urgent: false },
              { from: "제작팀", title: "DarkNova MV 예산", amount: "8,000만원", status: "긴급검토", urgent: true },
            ].map((item, i) => (
              <div key={i} className="px-4 py-3 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[#444444] text-[10px]">{item.from}</span>
                    <span className="text-[#CCCCCC] text-xs truncate">{item.title}</span>
                  </div>
                </div>
                <span className="text-[#666666] text-xs tabular-nums shrink-0">{item.amount}</span>
                <span className={`text-[9px] px-1.5 py-0.5 rounded border shrink-0 ${item.urgent ? "text-white/50 border-white/20" : "text-[#444444] border-white/[0.05]"}`}>{item.status}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          <SectionHeader title="이달 주요 세무 일정" />
          <div className="bg-[#141416] border border-white/[0.05] rounded-xl divide-y divide-white/[0.04]">
            {[
              { event: "부가가치세 신고", deadline: "D-7", amount: "미신고", urgent: true },
              { event: "원천세 납부", deadline: "D-15", amount: "280만원 추정", urgent: false },
              { event: "법인세 중간 예납", deadline: "D-42", amount: "사전 준비 필요", urgent: false },
            ].map((item, i) => (
              <div key={i} className="px-4 py-3 flex items-center gap-3">
                {item.urgent && <div className="w-1 h-4 bg-white/30 rounded-full shrink-0" />}
                {!item.urgent && <div className="w-1 h-4 rounded-full shrink-0" />}
                <div className="flex-1">
                  <div className="text-[#CCCCCC] text-xs">{item.event}</div>
                  <div className="text-[#555555] text-[10px]">{item.amount}</div>
                </div>
                <span className={`text-xs tabular-nums font-medium ${item.urgent ? "text-red-400/70" : "text-[#555555]"}`}>{item.deadline}</span>
              </div>
            ))}
          </div>

          <div className="bg-[#141416] border border-white/[0.05] rounded-xl px-4 py-3">
            <div className="text-[#555555] text-[10px] uppercase tracking-widest mb-2">크로스팀 재무 요청</div>
            {[
              { from: "A&R팀", req: "데모 예산 500만원 승인 요청", badge: "신규" },
              { from: "마케팅팀", req: "광고 집행 예산 조정 협의 요청", badge: "" },
            ].map((r, i) => (
              <div key={i} className="flex items-center gap-2 py-1.5 border-b border-white/[0.03] last:border-0">
                <span className="text-[10px] text-emerald-400/70 bg-emerald-400/10 px-1.5 py-0.5 rounded border border-emerald-400/20 shrink-0">{r.from}</span>
                <span className="text-[#555555] text-[11px] flex-1 truncate">{r.req}</span>
                {r.badge && <span className="text-[9px] text-[#7A9BFF] border border-[#4B6CF7]/20 px-1.5 py-0.5 rounded shrink-0">{r.badge}</span>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── 마케팅 대시보드 ───────────────────────────────────
export function MarketingDashboard() {
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-4 gap-3">
        <StatCard label="음원 차트 최고 순위" value="23위" sub="루나&스타즈" trend="↑ 14계단" />
        <StatCard label="SNS 팔로워" value="128,400" sub="총 팔로워" trend="이번 주 +2,100" />
        <StatCard label="보도자료 배포" value="3건" sub="이번 달" />
        <StatCard label="바이럴 지수" value="72점" sub="AI 예측 점수" trend="좋음" />
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* 이번 주 핵심 업무 */}
        <div>
          <SectionHeader title="이번 주 크로스팀 업무" />
          <div className="bg-[#141416] border border-white/[0.05] rounded-xl divide-y divide-white/[0.04]">
            {[
              { task: "DarkNova MV 예산 8,000만원 → 재무팀 협의", team: "재무팀", icon: "ri-money-dollar-circle-line", urgent: true },
              { task: "법무팀에 인플루언서 협찬 계약서 검토 의뢰", team: "법무팀", icon: "ri-file-text-line", urgent: false },
              { task: "A&R팀 DarkNova 앨범 콘셉트 무드보드 마케팅 활용 협의", team: "A&R팀", icon: "ri-image-line", urgent: false },
              { task: "CEO 주간 마케팅 보고 자료 작성 (차트·SNS)", team: "CEO", icon: "ri-bar-chart-line", urgent: true },
            ].map((item, i) => (
              <div key={i} className="px-4 py-3 flex items-center gap-3">
                <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-white/[0.04] shrink-0">
                  <i className={`${item.icon} text-[#555555] text-sm`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[#CCCCCC] text-xs leading-snug">{item.task}</div>
                  <div className="text-[#555555] text-[10px] mt-0.5">협업: {item.team}</div>
                </div>
                {item.urgent && <span className="text-[9px] text-white/40 border border-white/15 px-1.5 py-0.5 rounded shrink-0">긴급</span>}
              </div>
            ))}
          </div>
        </div>

        {/* 진행 중 캠페인 */}
        <div>
          <SectionHeader title="진행 중 캠페인" action={<a href="/marketing" className="text-[#444444] text-[10px] hover:text-[#666666] transition-colors cursor-pointer">워룸 →</a>} />
          <div className="bg-[#141416] border border-white/[0.05] rounded-xl divide-y divide-white/[0.04]">
            {[
              { name: "루나&스타즈 봄 시즌", platform: "Instagram · TikTok", score: "A+", status: "진행중", budget: "300만" },
              { name: "DarkNova 선공개 티저", platform: "YouTube · X", score: "B", status: "준비중", budget: "150만" },
              { name: "신인 오디션 홍보 캠페인", platform: "Instagram", score: "-", status: "기획중", budget: "80만" },
            ].map((c, i) => (
              <div key={i} className="px-4 py-3">
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-[#CCCCCC] text-xs">{c.name}</span>
                  <span className="text-[#888888] text-xs font-medium">{c.score}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[#555555] text-[10px]">{c.platform}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-[#444444] text-[10px]">{c.budget}</span>
                    <span className="text-[#444444] text-[10px] border border-white/[0.05] px-1.5 py-0.5 rounded">{c.status}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* 언론 모니터링 */}
          <div className="mt-3 bg-[#141416] border border-white/[0.05] rounded-xl px-4 py-3">
            <div className="text-[#555555] text-[10px] uppercase tracking-widest mb-2">오늘 언론 동향</div>
            {[
              { media: "아이돌차트", content: "루나&스타즈 차트 상승 기사 게재", tone: "긍정" },
              { media: "뮤직위크", content: "K-POP 시장 하반기 전망 분석", tone: "중립" },
            ].map((news, i) => (
              <div key={i} className="flex items-center gap-2 py-1.5">
                <span className={`text-[9px] px-1.5 py-0.5 rounded border shrink-0 ${news.tone === "긍정" ? "text-[#7A9BFF] border-[#4B6CF7]/20" : "text-[#444444] border-white/[0.05]"}`}>{news.tone}</span>
                <span className="text-[#555555] text-[10px] shrink-0">{news.media}</span>
                <span className="text-[#555555] text-[10px] truncate">{news.content}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── 법무 대시보드 ───────────────────────────────────
export function LegalDashboard() {
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-4 gap-3">
        <StatCard label="검토 대기 계약서" value="2건" sub="서명 전 상태" trend="독소조항 감지 1건" />
        <StatCard label="저작권 등록" value="8건" sub="총 등록 완료" />
        <StatCard label="법적 대응 중" value="1건" sub="표절 시비" trend="내용증명 수신" />
        <StatCard label="계약 갱신 D-day" value="D-23" sub="루나&스타즈 전속" />
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* 계약서 검토 + 크로스팀 업무 */}
        <div>
          <SectionHeader title="계약서 검토 대기" action={<a href="/legal" className="text-[#444444] text-[10px] hover:text-[#666666] transition-colors cursor-pointer">법무 데스크 →</a>} />
          <div className="bg-[#141416] border border-white/[0.05] rounded-xl divide-y divide-white/[0.04]">
            {[
              { title: "유통사 계약서 v2.3", from: "스카이뮤직 유통", warning: "수익 80% 조항 감지", urgent: true },
              { title: "광고 협찬 계약서", from: "서울전자 이과장", warning: "검토 전", urgent: false },
              { title: "인플루언서 마케팅 계약", from: "마케팅팀 의뢰", warning: "신규 접수", urgent: false },
            ].map((c, i) => (
              <div key={i} className="px-4 py-3 flex items-start gap-3">
                {c.urgent && <div className="w-1 h-4 bg-white/30 rounded-full mt-0.5 shrink-0" />}
                {!c.urgent && <div className="w-1 h-4 rounded-full mt-0.5 shrink-0" />}
                <div className="flex-1">
                  <div className="text-[#CCCCCC] text-xs font-medium">{c.title}</div>
                  <div className="text-[#555555] text-[10px]">{c.from}</div>
                  <div className={`text-[10px] mt-1 ${c.urgent ? "text-white/40" : "text-[#555555]"}`}>{c.warning}</div>
                </div>
                <a href="/legal" className="text-[10px] px-2 py-1 rounded bg-white/[0.05] text-[#666666] hover:text-[#AAAAAA] border border-white/[0.07] cursor-pointer whitespace-nowrap">검토 →</a>
              </div>
            ))}
          </div>
        </div>

        {/* 법무 크로스팀 + 리스크 */}
        <div className="space-y-3">
          <div>
            <SectionHeader title="타 부서 법무 요청" />
            <div className="bg-[#141416] border border-white/[0.05] rounded-xl divide-y divide-white/[0.04]">
              {[
                { from: "마케팅팀", req: "인플루언서 협찬 계약 표준안 제작 요청", date: "오늘" },
                { from: "A&R팀", req: "해외 작곡가 저작권 귀속 조항 검토", date: "내일까지" },
                { from: "CEO", req: "루나&스타즈 전속계약 갱신 조건안 제출", date: "D-3" },
              ].map((r, i) => (
                <div key={i} className="px-4 py-2.5 flex items-center gap-3">
                  <span className="text-[10px] text-[#7A9BFF] bg-[#4B6CF7]/10 border border-[#4B6CF7]/15 px-1.5 py-0.5 rounded shrink-0">{r.from}</span>
                  <span className="text-[#555555] text-[11px] flex-1 truncate">{r.req}</span>
                  <span className="text-[#555555] text-[10px] shrink-0">{r.date}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-red-400/[0.04] border border-red-400/15 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <i className="ri-alert-line text-red-400/70 text-sm"></i>
              <span className="text-red-400/70 text-xs font-medium">법적 리스크 현황</span>
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-xs">
                <span className="text-[#555555]">스타라이트엔터 표절 시비</span>
                <span className="text-red-400/60 text-[10px]">진행중</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-[#555555]">루나&스타즈 계약 갱신</span>
                <span className="text-amber-400/60 text-[10px]">D-23</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── 공연기획사 CEO 대시보드 ──────────────────────────────────────
export function LiveCEODashboard() {
  const { companyState } = useCompany();
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-4 gap-3">
        <StatCard label="가상 자본금" value={`${(companyState?.capital ?? 0).toLocaleString()}만원`} sub="현재 잔고" />
        <StatCard label="객석 점유율" value="83%" sub="평균 (4공연)" trend="목표 90%" />
        <StatCard label="스폰서 수익" value="5,500만원" sub="계약 완료" />
        <StatCard label="대기 결재" value="2건" sub="서명 필요" />
      </div>
      <div><SectionHeader title="공연 진행 현황" action={<a href="/concert-hub" className="text-[#444444] text-[10px] hover:text-[#666666] cursor-pointer">공연기획 →</a>} />
        <div className="bg-[#141416] border border-white/[0.05] rounded-xl divide-y divide-white/[0.04]">
          {[{name:"K-POP 서울 페스티벌",date:"2026.05.10",status:"기획 확정",tickets:"83%"},{name:"루나&스타즈 팬미팅",date:"2026.04.20",status:"티켓 판매중",tickets:"91%"}].map((c,i)=>(
            <div key={i} className="px-4 py-3 flex items-center gap-3">
              <div className="flex-1"><div className="text-[#CCCCCC] text-xs font-medium">{c.name}</div><div className="text-[#555555] text-[10px]">{c.date}</div></div>
              <span className="text-[#666666] text-xs">{c.tickets} 판매</span>
              <span className="text-[#444444] text-[10px] border border-white/[0.05] px-1.5 py-0.5 rounded">{c.status}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── 문화재단 CEO 대시보드 ────────────────────────────────────────
export function FoundationCEODashboard() {
  const { companyState } = useCompany();
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-4 gap-3">
        <StatCard label="보조금 잔액" value={`${(companyState?.capital ?? 0).toLocaleString()}만원`} sub="가용 잔액" />
        <StatCard label="진행 사업" value="3건" sub="수혜자 222명" />
        <StatCard label="보조금 수주" value="3,300만원" sub="올해 누계" trend="목표 5,000만" />
        <StatCard label="사회공헌 지수" value={`${companyState?.reputation ?? 0}점`} sub="/ 100점" />
      </div>
      <div><SectionHeader title="진행 중 지원 사업" action={<a href="/foundation-hub" className="text-[#444444] text-[10px] hover:text-[#666666] cursor-pointer">사업관리 →</a>} />
        <div className="bg-[#141416] border border-white/[0.05] rounded-xl divide-y divide-white/[0.04]">
          {[{name:"청년 예술가 창작 지원",progress:70,beneficiaries:42},{name:"지역 문화예술 교육",progress:78,beneficiaries:180}].map((p,i)=>(
            <div key={i} className="px-4 py-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[#CCCCCC] text-xs font-medium">{p.name}</span>
                <span className="text-[#7A9BFF] text-xs">{p.beneficiaries}명 수혜</span>
              </div>
              <div className="w-full bg-white/[0.04] rounded-full h-px"><div className="h-px rounded-full bg-[#4B6CF7]/40" style={{width:`${p.progress}%`}} /></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── 웹툰스튜디오 CEO 대시보드 ────────────────────────────────────
export function WebtoonCEODashboard() {
  const { companyState } = useCompany();
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-4 gap-3">
        <StatCard label="가상 자본금" value={`${(companyState?.capital ?? 0).toLocaleString()}만원`} sub="현재 잔고" />
        <StatCard label="연재 작품" value="2개" sub="진행중" trend="완결 2개" />
        <StatCard label="IP 라이선스" value="9,500만원" sub="계약 완료" />
        <StatCard label="총 독자" value="1,107만" sub="누적" trend="월 +8.2만" />
      </div>
      <div><SectionHeader title="IP 라이선싱 파이프라인" action={<a href="/ip-hub" className="text-[#444444] text-[10px] hover:text-[#666666] cursor-pointer">IP 스튜디오 →</a>} />
        <div className="bg-[#141416] border border-white/[0.05] rounded-xl divide-y divide-white/[0.04]">
          {[{work:"어둠 속의 빛",type:"OTT 판권",partner:"Netflix Korea",amount:"8,000만원",status:"협상중"},{work:"수상한 가족",type:"게임 IP",partner:"넥슨",amount:"3,000만원",status:"계약완료"}].map((l,i)=>(
            <div key={i} className="px-4 py-3 flex items-center gap-3">
              <div className="flex-1"><div className="text-[#CCCCCC] text-xs font-medium">{l.work}</div><div className="text-[#555555] text-[10px]">{l.type} · {l.partner}</div></div>
              <span className="text-[#666666] text-xs tabular-nums">{l.amount}</span>
              <span className={`text-[9px] px-1.5 py-0.5 rounded border ${l.status==="계약완료"?"text-[#7A9BFF] border-[#4B6CF7]/20":"text-[#555555] border-white/[0.05]"}`}>{l.status}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── 광고에이전시 CEO 대시보드 ────────────────────────────────────
export function AgencyCEODashboard() {
  const { companyState } = useCompany();
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-4 gap-3">
        <StatCard label="가상 자본금" value={`${(companyState?.capital ?? 0).toLocaleString()}만원`} sub="현재 잔고" />
        <StatCard label="진행 캠페인" value="3건" sub="이번 달" />
        <StatCard label="수주 금액" value="3,150만원" sub="이번 달" trend="전월 대비 +22%" />
        <StatCard label="평균 CTR" value="3.2%" sub="캠페인 평균" trend="업계 평균 1.8%" />
      </div>
      <div><SectionHeader title="진행 중 캠페인" action={<a href="/campaign-hub" className="text-[#444444] text-[10px] hover:text-[#666666] cursor-pointer">캠페인 허브 →</a>} />
        <div className="bg-[#141416] border border-white/[0.05] rounded-xl divide-y divide-white/[0.04]">
          {[{name:"서울전자 신제품 론칭",client:"서울전자",budget:"1,500만원",status:"진행중"},{name:"루나&스타즈 MV 제작",client:"K-ArtPEN",budget:"800만원",status:"후반작업"}].map((c,i)=>(
            <div key={i} className="px-4 py-3 flex items-center gap-3">
              <div className="flex-1"><div className="text-[#CCCCCC] text-xs font-medium">{c.name}</div><div className="text-[#555555] text-[10px]">{c.client}</div></div>
              <span className="text-[#666666] text-xs tabular-nums">{c.budget}</span>
              <span className="text-[#444444] text-[10px] border border-white/[0.05] px-1.5 py-0.5 rounded">{c.status}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
